import { ReactElement, useCallback, useEffect, useState } from 'react';
import { ResultOption } from './styles';
import {
  IndeterminateResultIcon,
  NegativeResultIcon,
  PositiveResultIcon,
} from '@root/src/assets/images';
import Lang from '@lang/en';
import { SelectOption } from '@root/src/app/common/types';
import useSaveResult from '../person/useSaveResult';
import ResultsComponentDesktop from './resultsDesktop';
import useWindowWidth from '@root/src/app/common/useWindowWidth';
import ResultsComponentMobile from './resultsMobile';
import { ResultOptionType } from '../../order/types';
import {
  dateFriendlyFormat,
  dayjs,
  formatUTCStringAsApiTimestamp,
  parseApiFormat,
  SelectOptionI,
} from '@app/utils';
import dayjsLib from 'dayjs';
import { NullableString } from '@app/common/types';

export interface ResultsComponentI extends PreviewPropsI {
  orderId: number;
  fullName: string;
  handleClose: () => void;
  handleSave: () => void;
  handleConfirm: () => void;
  isDisabledConfirm: boolean;
  isDisabledSave: boolean;
  setImageUrl: (imageUrl: string) => void;
  clearImageUrl: () => void;
  imageUrl: string;
  method: string;
  setMethod: (methodText: string) => void;
  page: number;
  error: string;
  result: SelectOption | undefined;
  onResultChange: React.Dispatch<
    React.SetStateAction<SelectOption | undefined>
  >;
  resultOptions: SelectOption[];
  testDate: NullableString;
  testHours: SelectOptionI;
  testMinutes: SelectOptionI;
  setTestDate: (value: string) => void;
  setTestHours: (value: SelectOptionI) => void;
  setTestMinutes: (value: SelectOptionI) => void;
  reportedDate: NullableString;
  reportedHours: SelectOptionI;
  reportedMinutes: SelectOptionI;
  setReportedDate: (value: string) => void;
  setReportedHours: (value: SelectOptionI) => void;
  setReportedMinutes: (value: SelectOptionI) => void;
  reportedDateEarlierTestDate: boolean;
  currentDateEarlierSelectedDate: boolean;
}

interface PreviewPropsI {
  closePreviewModal: () => void;
  openPreviewModal: () => void;
  setZoomImage: (value: boolean) => void;
  zoomImage: boolean;
  setIsPreviewModalOpened: (value: boolean) => void;
  isPreviewModalOpened: boolean;
}

export const onDateChangeHandler = (
  value: Date | null,
  dateSetter: (date: string) => void
): void => {
  if (!value) {
    dateSetter('');
    return;
  }
  const formattedDate = formatUTCStringAsApiTimestamp(
    value,
    dateFriendlyFormat
  );
  dateSetter(formattedDate);
};

export const resultOptions: SelectOption[] = [
  {
    label: (
      <ResultOption data-testid="healthcheck-add-result-dropdown-option-negative">
        <NegativeResultIcon />
        <span>{Lang.healthCheckResultNegativeLabel}</span>
      </ResultOption>
    ),
    value: 'negative',
  },
  {
    label: (
      <ResultOption data-testid="healthcheck-add-result-dropdown-option-positive">
        <PositiveResultIcon />
        <span>{Lang.healthCheckResultPositiveLabel}</span>
      </ResultOption>
    ),
    value: 'positive',
  },
  {
    label: (
      <ResultOption data-testid="healthcheck-add-result-dropdown-option-indeterminate">
        <IndeterminateResultIcon />
        <span>{Lang.healthCheckResultIndeterminateLabel}</span>
      </ResultOption>
    ),
    value: 'indeterminate',
  },
];

export const ResultsComponent = (props: ResultsComponentI): ReactElement => {
  const { isMobileView } = useWindowWidth();

  return isMobileView ? (
    <ResultsComponentMobile {...props} />
  ) : (
    <ResultsComponentDesktop {...props} />
  );
};

const buildDateTime = (
  baseDate: Date,
  hours: number,
  minutes: number
): dayjsLib.Dayjs => dayjsLib(baseDate).hour(hours).minute(minutes).second(0);

const Results = ({
  orderId,
  handleClose,
  fullName,
  isImageRequired,
  setShouldReload,
  setImageLoaded,
  methodText,
}: {
  orderId: number;
  handleClose: () => void;
  fullName: string;
  isImageRequired?: boolean;
  setShouldReload: (value: boolean) => void;
  setImageLoaded?: (value: boolean) => void;
  methodText?: string;
}): ReactElement => {
  const [imageUrl, setImageUrl] = useState('');
  const [result, setResult] = useState<SelectOption>();
  const [error, setError] = useState('');
  const [method, setMethod] = useState(methodText || '');
  const [page, setPage] = useState(1);
  const [isDisabledConfirm, setDisabledConfirm] = useState(true);
  const [isDisabledSave, setDisabledSave] = useState(true);

  const currentDate = formatUTCStringAsApiTimestamp(
    new Date(),
    dateFriendlyFormat
  );

  const currentHour = new Date().getHours();
  const currentMinutes = new Date().getMinutes();

  const [testDate, setTestDate] = useState(currentDate);
  const [testHours, setTestHours] = useState({
    label: `${currentHour}`,
    value: `${currentHour}`,
  });
  const [testMinutes, setTestMinutes] = useState({
    label: `${currentMinutes}`,
    value: `${currentMinutes}`,
  });

  const [reportedDate, setReportedDate] = useState(currentDate);
  const [reportedHours, setReportedHours] = useState({
    label: `${currentHour}`,
    value: `${currentHour}`,
  });
  const [reportedMinutes, setReportedMinutes] = useState({
    label: `${currentMinutes}`,
    value: `${currentMinutes}`,
  });

  const [
    reportedDateEarlierTestDate,
    setReportedDateEarlierTestDate,
  ] = useState(false);

  const [
    currentDateEarlierSelectedDate,
    setCurrentDateEarlierSelectedDate,
  ] = useState(false);

  const { saveResult } = useSaveResult();

  const sampleTakenAt = dayjs(
    buildDateTime(
      new Date(testDate),
      Number(testHours.value),
      Number(testMinutes.value)
    )
  ).valueOf();

  const staticSampleTakenAt = formatUTCStringAsApiTimestamp(
    sampleTakenAt,
    parseApiFormat
  );

  const reportedAt = dayjs(
    buildDateTime(
      new Date(reportedDate),
      Number(reportedHours.value),
      Number(reportedMinutes.value)
    )
  ).valueOf();

  const staticReportedAt = formatUTCStringAsApiTimestamp(
    reportedAt,
    parseApiFormat
  );

  useEffect(() => {
    setReportedDateEarlierTestDate(false);
    setCurrentDateEarlierSelectedDate(false);
  }, [reportedAt, sampleTakenAt]);

  useEffect(() => {
    if (page === 1 && reportedAt && sampleTakenAt) {
      setDisabledConfirm(false);
    }

    if (page === 2) {
      setDisabledConfirm(!Boolean(result));
    }

    setDisabledSave(!method.trim().length);
  }, [reportedAt, sampleTakenAt, result, method, page]);

  const handleSetImage = (value: string): void => {
    setImageUrl(value);
    setImageLoaded && setImageLoaded(true);
  };

  const handleClearImage = (): void => {
    setImageUrl('');
    setImageLoaded && setImageLoaded(false);
  };

  const handleConfirm = useCallback(() => {
    const currentDateTime = dayjs(
      buildDateTime(
        new Date(currentDate),
        Number(currentHour),
        Number(currentMinutes)
      )
    ).valueOf();

    const simpleValidation = () => {
      if (!result && !sampleTakenAt && !reportedAt) {
        return Lang.healthCheckMissingResultText;
      }
      return '';
    };
    if (sampleTakenAt > currentDateTime || reportedAt > currentDateTime) {
      setCurrentDateEarlierSelectedDate(true);
      return;
    }
    if (sampleTakenAt > reportedAt) {
      setReportedDateEarlierTestDate(true);
      return;
    }

    const errorMessage = simpleValidation();
    setError(errorMessage);
    if (errorMessage !== '') {
      return;
    }

    setPage(page + 1);
  }, [
    saveResult,
    orderId,
    result,
    imageUrl,
    setShouldReload,
    handleClose,
    sampleTakenAt,
    reportedAt,
    method,
  ]);

  const handleSave = useCallback(async () => {
    if (method.trim().length === 0) {
      return;
    }

    await saveResult(
      orderId,
      result?.value as ResultOptionType,
      imageUrl,
      sampleTakenAt,
      staticSampleTakenAt,
      reportedAt,
      staticReportedAt,
      method
    );
    setShouldReload(true);
    handleClose();
  }, [
    saveResult,
    orderId,
    result,
    imageUrl,
    setShouldReload,
    handleClose,
    sampleTakenAt,
    reportedAt,
    method,
  ]);

  const [isPreviewModalOpened, setIsPreviewModalOpened] = useState(false);
  const [zoomImage, setZoomImage] = useState(false);
  const openPreviewModal = () => setIsPreviewModalOpened(true);
  const closePreviewModal = () => {
    setIsPreviewModalOpened(false);
    setZoomImage(false);
  };

  const resultProps: ResultsComponentI = {
    orderId,
    fullName,
    setImageUrl: handleSetImage,
    clearImageUrl: handleClearImage,
    handleClose,
    handleSave,
    handleConfirm,
    isDisabledConfirm,
    isDisabledSave,
    imageUrl,
    method,
    setMethod,
    page,
    result,
    error,
    resultOptions,
    onResultChange: setResult,
    closePreviewModal,
    openPreviewModal,
    setZoomImage,
    zoomImage,
    setIsPreviewModalOpened,
    isPreviewModalOpened,
    testDate,
    testHours,
    testMinutes,
    setTestDate,
    setTestHours,
    setTestMinutes,
    reportedDate,
    reportedHours,
    reportedMinutes,
    setReportedDate,
    setReportedHours,
    setReportedMinutes,
    reportedDateEarlierTestDate,
    currentDateEarlierSelectedDate,
  };

  return <ResultsComponent {...resultProps} />;
};

export default Results;
